import React, { useRef, useEffect, useState } from 'react';
import Editor, { DiffEditor, Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import { useProjectStore } from '../../stores/projectStore';
import { useIDEStore } from '../../stores/ideStore';
import { useAuthStore } from '../../stores/authStore';
import { WebSocketService } from '../../services/WebSocketService';
import { X, Check, XCircle, Code, ChevronRight, Save, Sparkles } from 'lucide-react';

interface CodeEditorPanelProps {
  className?: string;
}

export const CodeEditorPanel: React.FC<CodeEditorPanelProps> = ({ className }) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  
  const { currentProject, currentFile, updateFile, openFile, closeFile } = useProjectStore();
  const { theme } = useIDEStore();
  const { user } = useAuthStore();
  
  const [openTabs, setOpenTabs] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<{path: string, original: string, modified: string} | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sync open tabs with current file
  useEffect(() => {
    if (currentFile && !openTabs.includes(currentFile.path)) {
      setOpenTabs(prev => [...prev, currentFile.path]);
      setActiveTab(currentFile.path);
    } else if (currentFile) {
      setActiveTab(currentFile.path);
    }
  }, [currentFile]);

  // Handle file open request from other panels
  useEffect(() => {
    // If we have a pending change request (from AI), set it up
    // This is a placeholder for where we'd hook into the AI 'Apply' event if it sent structured data directly here
    // For now, we assume direct updates via updateFile, but we could intercept them here for Diff View
  }, []);

  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;
    
    // Add shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSave();
    });
  };

  const handleEditorChange = (value: string | undefined) => {
    if (activeTab && value !== undefined) {
      updateFile(activeTab, value);
    }
  };

  const handleSave = () => {
    if (activeTab && currentProject) {
      setIsSaving(true);
      const fileContent = currentProject.files.find(f => f.path === activeTab)?.content || '';
      const userId = user?.id || localStorage.getItem('userId') || 'unknown'; // Fallback

      console.log('Saving file to backend:', activeTab);
      
      try {
        WebSocketService.saveFile(
          currentProject.id,
          userId,
          activeTab,
          fileContent
        );
        
        // Show temporary success indicator (could be better UX)
        setTimeout(() => setIsSaving(false), 500);
      } catch (e) {
        console.error('Failed to save file:', e);
        setIsSaving(false);
      }
    }
  };

  const closeTab = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newTabs = openTabs.filter(t => t !== path);
    setOpenTabs(newTabs);
    
    if (activeTab === path) {
      const nextTab = newTabs[newTabs.length - 1] || null;
      setActiveTab(nextTab);
      if (nextTab) {
        openFile(nextTab);
      } else {
        closeFile(); // Clear current file in store
      }
    }
  };

  const getLanguage = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      'cs': 'csharp',
      'js': 'javascript',
      'ts': 'typescript',
      'json': 'json',
      'md': 'markdown',
      'html': 'html',
      'css': 'css',
      'shader': 'glsl',
      'hlsl': 'hlsl',
      'unity': 'yaml',
      'prefab': 'yaml',
      'mat': 'yaml',
    };
    return languageMap[ext || ''] || 'plaintext';
  };

  const activeFileObj = currentProject?.files.find(f => f.path === activeTab);

  // Mock function to simulate AI proposing a change (for demonstration/testing)
  const simulateAIChange = () => {
    if (!activeFileObj) return;
    setPendingChanges({
      path: activeFileObj.path,
      original: activeFileObj.content,
      modified: activeFileObj.content + '\n// AI Generated Comment\n// Improved logic here'
    });
  };

  const acceptChanges = () => {
    if (pendingChanges) {
      updateFile(pendingChanges.path, pendingChanges.modified);
      setPendingChanges(null);
    }
  };

  const rejectChanges = () => {
    setPendingChanges(null);
  };

  return (
    <div className={`code-editor-panel ${className || ''} flex flex-col h-full bg-[#1e1e1e]`}>
      {/* Tabs Bar */}
      <div className="flex bg-[#252526] overflow-x-auto no-scrollbar">
        {openTabs.map(path => {
          const fileName = path.split('/').pop() || path;
          const isActive = activeTab === path;
          const isPending = pendingChanges?.path === path;
          
          return (
            <div
              key={path}
              className={`
                group flex items-center gap-2 px-3 py-2 min-w-[120px] max-w-[200px] text-xs cursor-pointer border-r border-[#1e1e1e] select-none
                ${isActive ? 'bg-[#1e1e1e] text-white border-t-2 border-t-[#007acc]' : 'bg-[#2d2d2d] text-gray-400 hover:bg-[#2a2a2a]'} 
                ${isPending ? 'border-t-2 border-t-yellow-500' : ''}
              `}
              onClick={() => {
                setActiveTab(path);
                openFile(path);
              }}
            >
              <span className={`
                ${path.endsWith('.cs') ? 'text-green-400' : 'text-blue-400'}
              `}>
                {getFileIcon(path)}
              </span>
              <span className="truncate flex-1">{fileName}</span>
              {/* Unsaved indicator (dot) or Close button on hover */}
              <div className="w-4 h-4 flex items-center justify-center rounded-sm hover:bg-[#444]" onClick={(e) => closeTab(path, e)}>
                <X size={12} className={`opacity-0 group-hover:opacity-100 ${isActive ? 'opacity-100' : ''}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Breadcrumbs */}
      {activeTab && (
        <div className="h-6 flex items-center px-4 bg-[#1e1e1e] border-b border-[#333] text-xs text-gray-500">
          {activeTab.split('/').map((part, i, arr) => (
            <React.Fragment key={i}>
              <span className={`hover:text-white cursor-pointer ${i === arr.length - 1 ? 'text-white font-medium' : ''}`}>
                {part}
              </span>
              {i < arr.length - 1 && <ChevronRight size={12} className="mx-1 opacity-50" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 relative">
        {pendingChanges && activeTab === pendingChanges.path ? (
          <div className="absolute inset-0 flex flex-col z-10">
            {/* Diff Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-[#333]">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-yellow-400" />
                <span className="text-xs font-bold text-white">AI Suggestion</span>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={rejectChanges}
                  className="flex items-center gap-1 px-3 py-1 text-xs bg-red-900/30 text-red-200 border border-red-900/50 rounded hover:bg-red-900/50"
                >
                  <XCircle size={12} /> Reject
                </button>
                <button 
                  onClick={acceptChanges}
                  className="flex items-center gap-1 px-3 py-1 text-xs bg-green-900/30 text-green-200 border border-green-900/50 rounded hover:bg-green-900/50"
                >
                  <Check size={12} /> Accept
                </button>
              </div>
            </div>
            {/* Diff Editor */}
            <DiffEditor
              height="100%"
              language={getLanguage(pendingChanges.path)}
              original={pendingChanges.original}
              modified={pendingChanges.modified}
              theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
              options={{
                renderSideBySide: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                fontFamily: 'Share Tech Mono, monospace',
              }}
            />
          </div>
        ) : activeFileObj ? (
          <Editor
            height="100%"
            language={getLanguage(activeFileObj.path)}
            value={activeFileObj.content}
            theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            options={{
              minimap: { enabled: true },
              fontSize: 13,
              fontFamily: 'Share Tech Mono, monospace',
              lineNumbers: 'on',
              roundedSelection: false,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 4,
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Code size={48} className="mb-4 opacity-20" />
            <p>No file open</p>
            <p className="text-xs mt-2">Select a file from the Assets panel</p>
          </div>
        )}
      </div>
      
      {/* Footer / Status Bar for Editor */}
      {activeTab && (
        <div className="h-6 bg-[#007acc] text-white text-[10px] flex items-center px-2 justify-end gap-4 select-none">
          {isSaving && (
            <span className="flex items-center gap-1 animate-pulse">
              <Save size={10} /> Saving...
            </span>
          )}
          <span>Ln 1, Col 1</span>
          <span>UTF-8</span>
          <span>{getLanguage(activeTab).toUpperCase()}</span>
        </div>
      )}
    </div>
  );
};

const getFileIcon = (path: string) => {
  const ext = path.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'cs': return <span className="font-bold">C#</span>;
    case 'js': return <span className="font-bold text-yellow-400">JS</span>;
    case 'json': return <span className="font-bold text-orange-400">{{}}</span>;
    default: return <Code size={12} />;
  }
};